/* Native fixed duos. Shopify owns composition, inventory, prices and discounts.
   Quote cart identities stay in memory only until their lines have been removed. */
(() => {
  const catalogueQuery = `query K9FixedBundle($id: ID!, $country: CountryCode!, $cursor: String) @inContext(country: $country) {
    product(id: $id) { id variants(first: 250, after: $cursor) { nodes {
      id availableForSale requiresComponents price { amount currencyCode }
      components(first: 3) { nodes { quantity productVariant { id price { amount currencyCode } product { id } } }
        pageInfo { hasNextPage } }
    } pageInfo { hasNextPage endCursor } } }
  }`;
  const quoteQuery = `mutation K9FixedQuote($input: CartInput!, $country: CountryCode!) @inContext(country: $country) {
    cartCreate(input: $input) { cart { id lines(first: 2) { nodes {
      id __typename quantity merchandise { ... on ProductVariant { id } }
      cost { subtotalAmount { amount currencyCode } totalAmount { amount currencyCode } }
      discountAllocations { __typename discountedAmount { amount currencyCode }
        discountApplication { targetType value { __typename ... on PricingPercentageValue { percentage } ... on MoneyV2 { amount currencyCode } } }
        ... on CartAutomaticDiscountAllocation { title } }
      ... on ComponentizableCartLine { lineComponents { quantity merchandise { ... on ProductVariant { id } } } }
    } pageInfo { hasNextPage } } } userErrors { code } warnings { code } }
  }`;
  const clearQuery = `mutation K9FixedClear($cart: ID!, $lines: [ID!]!, $country: CountryCode!) @inContext(country: $country) {
    cartLinesRemove(cartId: $cart, lineIds: $lines) { cart { totalQuantity } userErrors { code } }
  }`;
  let quoteQueue = Promise.resolve(), quoteUncertain = false;
  const pendingQuotes = new Map();
  const numericId = gid => {
    const id = Number(String(gid).split('/').pop());
    if (!Number.isSafeInteger(id) || id <= 0) throw new Error('id');
    return id;
  };

  function register() {
    if (!window.K9NativeOffer || typeof window.K9NativeOffer.rateOf !== 'function' || customElements.get('k9-fixed-offer')) return;
    // Lot 6b-1: the displayed rate is the percentage of the Shopify discount, read by the shared rateOf.
    const { Base, state, rateOf } = window.K9NativeOffer;
    class K9FixedOffer extends Base {
      static requiresOptions = false;
      static validation() { return null; }
      nativeFields() { return {}; }
      synchronise() {}
      reflect() {}
      showFieldError() {}

      static selected(config, choices) {
        return config.bundle.variants?.find(variant => variant.available && ['primary', 'secondary'].every(key =>
          variant.components[key] === Number(choices[key])
          && config.products[key].variants.some(v => v.available && v.id === Number(choices[key]))));
      }
      static items(config, mode, choices) {
        const variant = mode === 'bundle' && this.selected(config, choices);
        return variant ? [{ id: variant.id, quantity: 1 }] : null;
      }

      connectedCallback() {
        this.preparationActive = false;
        this.quotes = new Map();
        this.quoteErrors = new Set();
        this.quoteRequests = new Map();
        this.catalogueFailed = false;
        this.cataloguePromise = null;
        this.failedVisible = false;
        this.generation = (this.generation || 0) + 1;
        super.connectedCallback();
        const activate = () => { this.preparationActive = true; this.update(); };
        if (window.K9OfferActivation) window.K9OfferActivation(this, activate); else activate();
      }
      disconnectedCallback() { super.disconnectedCallback(); this.generation++; }

      money(value) {
        return new Intl.NumberFormat(this.config.locale, { style: 'currency', currency: this.config.currency }).format(value / 100);
      }
      cents(money) {
        if (money?.currencyCode !== this.config.currency || !/^\d+(?:\.\d{1,2})?$/.test(money.amount)) throw new Error('currency');
        const amount = Math.round(Number(money.amount) * 100);
        if (!Number.isSafeInteger(amount)) throw new Error('amount');
        return amount;
      }
      async storefront(query, variables) {
        const endpoint = new URL(this.config.storefrontUrl, location.origin);
        if (endpoint.origin !== location.origin) throw new Error('endpoint');
        const { response, data } = await Base.json(endpoint.href, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ query, variables }),
        });
        if (!response.ok || data.errors?.length || !data.data) throw new Error('storefront');
        return data.data;
      }
      async readCatalogue() {
        const nodes = [], cursors = new Set();
        let cursor = null;
        for (;;) {
          const { product } = await this.storefront(catalogueQuery, {
            id: `gid://shopify/Product/${this.config.bundle.id}`, country: this.config.country, cursor,
          });
          const page = product?.variants;
          if (!product || numericId(product.id) !== this.config.bundle.id || !Array.isArray(page?.nodes)
            || !page.nodes.length || page.nodes.length > 250 || typeof page.pageInfo?.hasNextPage !== 'boolean') throw new Error('bundle');
          nodes.push(...page.nodes);
          if (nodes.length > 2048) throw new Error('bundle');
          if (!page.pageInfo.hasNextPage) break;
          cursor = page.pageInfo.endCursor;
          if (typeof cursor !== 'string' || !cursor || cursors.has(cursor) || cursors.size >= 8) throw new Error('bundle');
          cursors.add(cursor);
        }
        const ids = new Set(), pairs = new Set();
        const variants = nodes.map(variant => {
          const source = variant.components;
          if (!variant.requiresComponents || source.pageInfo.hasNextPage || source.nodes.length !== 2
            || source.nodes.some(c => c.quantity !== 1)) throw new Error('composition');
          const components = {};
          let sum = 0;
          for (const key of ['primary', 'secondary']) {
            const component = source.nodes.find(c => numericId(c.productVariant.product.id) === this.config.products[key].id);
            if (!component) throw new Error('component');
            const id = numericId(component.productVariant.id);
            const known = this.config.products[key].variants.find(v => v.id === id);
            const price = this.cents(component.productVariant.price);
            if (!known || known.price !== price) throw new Error('component-price');
            components[key] = id;
            sum += price;
          }
          const price = this.cents(variant.price);
          // A component price change must be reconciled in admin, never hidden by stale sale copy.
          if (price !== sum) throw new Error('parent-price');
          const id = numericId(variant.id), pair = `${components.primary}:${components.secondary}`;
          if (ids.has(id) || pairs.has(pair)) throw new Error('bundle');
          ids.add(id); pairs.add(pair);
          return { id, price, available: variant.availableForSale, components };
        });
        if (variants.length !== this.config.products.primary.variants.length * this.config.products.secondary.variants.length) throw new Error('bundle');
        return variants;
      }
      async requestQuote(variant, isCurrent = () => true) {
        if (this.quoteRequests.has(variant.id)) return this.quoteRequests.get(variant.id);
        const generation = this.generation;
        const request = quoteQueue.then(async () => {
          if (!isCurrent()) return null;
          if (quoteUncertain || pendingQuotes.size) throw new Error('quote-cleanup');
          let created;
          try {
            quoteUncertain = true;
            const { cartCreate: result } = await this.storefront(quoteQuery, {
              country: this.config.country,
              input: { buyerIdentity: { countryCode: this.config.country }, lines: [
                { merchandiseId: `gid://shopify/ProductVariant/${variant.id}`, quantity: 1 },
              ] },
            });
            const cart = result?.cart;
            if (typeof cart?.id === 'string' && cart.id && Array.isArray(cart.lines?.nodes)
              && cart.lines.nodes.every(line => typeof line.id === 'string' && line.id)) {
              created = { id: cart.id, lines: cart.lines.nodes.map(line => line.id) };
              pendingQuotes.set(created.id, created.lines);
              quoteUncertain = false;
            } else if (cart === null && Array.isArray(result.userErrors) && result.userErrors.length) quoteUncertain = false;
            if (!created) throw new Error('quote');
            const lines = result.cart?.lines;
            if (result.userErrors.length || result.warnings.length || !lines || lines.pageInfo.hasNextPage || lines.nodes.length !== 1) throw new Error('quote');
            const line = lines.nodes[0];
            if (line.__typename !== 'ComponentizableCartLine' || numericId(line.merchandise.id) !== variant.id || line.quantity !== 1
              || line.lineComponents.length !== 2 || line.lineComponents.some(c => c.quantity !== 1)
              || !Object.values(variant.components).every(id => line.lineComponents.some(c => numericId(c.merchandise.id) === id))) throw new Error('quote-composition');
            const original = this.cents(line.cost.subtotalAmount);
            const total = this.cents(line.cost.totalAmount);
            // Exactly one allocation, automatic and in percent (rateOf); its amount must be that rate of the bundle.
            if (!Array.isArray(line.discountAllocations) || line.discountAllocations.length !== 1) throw new Error('discount');
            const [allocation] = line.discountAllocations;
            const rate = rateOf(allocation);
            const discount = this.cents(allocation.discountedAmount);
            if (original !== variant.price || discount !== Math.round(original * rate / 100)
              || total !== original - discount || discount <= 0) throw new Error('discount');
            const quote = { id: variant.id, original, total, rate };
            if (generation === this.generation && this.isConnected) {
              this.quotes.set(variant.id, quote);
              this.quoteErrors.delete(variant.id);
            }
            return quote;
          } finally {
            if (created) {
              const { cartLinesRemove: cleared } = await this.storefront(clearQuery, {
                country: this.config.country, cart: created.id, lines: created.lines,
              });
              if (!Array.isArray(cleared?.userErrors) || cleared.userErrors.length || cleared.cart?.totalQuantity !== 0) throw new Error('quote-cleanup');
              pendingQuotes.delete(created.id);
            }
          }
        });
        quoteQueue = request.catch(() => {});
        this.quoteRequests.set(variant.id, request);
        try { return await request; }
        catch (error) {
          if (generation === this.generation) { this.quoteErrors.add(variant.id); this.quotes.delete(variant.id); }
          throw error;
        }
        finally {
          if (generation === this.generation) {
            this.quoteRequests.delete(variant.id);
            if (this.isConnected) this.update();
          }
        }
      }
      update() {
        if (!this.config) return;
        for (const select of this.querySelectorAll('[data-variant]')) this.draft.choices[select.dataset.variant] = select.value;
        for (const container of this.querySelectorAll('[data-product-image]')) {
          const variant = this.config.products[container.dataset.productImage].variants.find(v => String(v.id) === this.draft.choices[container.dataset.productImage]);
          const image = container.querySelector('img');
          if (variant?.image && image && image.getAttribute('src') !== variant.image) { image.removeAttribute('srcset'); image.src = variant.image; }
        }
        if (this.preparationActive !== false && !this.config.bundle.variants && !this.cataloguePromise && !this.catalogueFailed) {
          const generation = this.generation;
          this.cataloguePromise = this.readCatalogue().then(variants => {
            if (generation !== this.generation || !this.isConnected) return;
            this.config.bundle.variants = variants;
            this.update();
          }).catch(() => { if (generation === this.generation) this.catalogueFailed = true; });
        }
        const variant = this.constructor.selected(this.config, this.draft.choices);
        const quote = variant && this.quotes.get(variant.id);
        if (this.preparationActive !== false && variant && !quote && !this.quoteErrors.has(variant.id) && !this.quoteRequests.has(variant.id)) {
          const generation = this.generation;
          this.requestQuote(variant, () => this.isConnected && this.generation === generation
            && this.constructor.selected(this.config, this.draft.choices)?.id === variant.id).catch(() => {});
        }
        if (quote) {
          this.querySelector('[data-total]').textContent = this.money(quote.total);
          this.querySelector('[data-original]').textContent = this.money(quote.original);
          // Always from the Liquid template, never from the text already shown (a later quote may change the rate).
          const badge = this.querySelector('[data-discount]');
          badge.textContent = String(badge.dataset.rateTemplate || '').split('{rate}').join(String(quote.rate));
          this.hidden = false;
          this.failedVisible = true;
          if (!this.draft.blocked && [this.config.text.beforeError, this.config.text.unavailable].includes(this.draft.message)) this.message('');
        } else if (!this.draft.blocked) this.hidden = !this.failedVisible;
        if (!variant && this.failedVisible && !this.draft.blocked) this.message(this.config.text.unavailable);
        this.querySelector('[data-quoted-total]').hidden = !quote;
        this.querySelector('[data-discount]').hidden = !quote;
        this.button.disabled = state.pending || this.preparing || !variant || this.draft.blocked || this.quoteRequests.has(variant?.id);
        this.label.textContent = state.pending || this.preparing ? this.config.text.busy : this.defaultLabel;
        this.button.setAttribute('aria-disabled', String(this.button.disabled));
        for (const control of this.querySelectorAll('input, select')) control.disabled = state.pending || Boolean(this.preparing);
      }
      validateAdded(cart, items) {
        const quote = this.purchaseQuote;
        return Boolean(quote && items.length === 1 && quote.id === items[0].id
          && cart.items.filter(line => line.id === quote.id && Base.propertiesKey(line.properties) === '[]').every(line =>
            line.has_components && line.original_price === quote.original && line.final_price <= quote.total));
      }
      async submit() {
        if (state.pending || this.preparing || this.draft.blocked) return;
        const variant = this.constructor.selected(this.config, this.draft.choices);
        if (!variant) return;
        // Lot 6b-1: the purchase must match the quote the buyer saw (rate, price before and after discount).
        const shown = this.quotes.get(variant.id);
        this.preparing = true;
        this.update();
        let quoteFailed = false;
        try {
          if (!shown) throw new Error('quote-missing');
          // Recheck current component prices and composition, then quote on every purchase.
          this.config.bundle.variants = await this.readCatalogue();
          const fresh = this.constructor.selected(this.config, this.draft.choices);
          if (!fresh || fresh.id !== variant.id) throw new Error('changed');
          this.purchaseQuote = await this.requestQuote(fresh);
          if (this.constructor.selected(this.config, this.draft.choices)?.id !== variant.id || !this.isConnected) return;
          if (this.purchaseQuote.rate !== shown.rate || this.purchaseQuote.original !== shown.original
            || this.purchaseQuote.total !== shown.total) throw new Error('quote-changed');
        } catch {
          this.quotes.delete(variant.id);
          // A failed fresh catalogue must not trigger a quote using its old mapping in update().
          this.quoteErrors.add(variant.id);
          this.failedVisible = true;
          quoteFailed = true;
          this.message(this.config.text.beforeError);
          return;
        } finally {
          this.preparing = false;
          if (this.isConnected) {
            this.update();
            if (quoteFailed) this.querySelector('[data-status]')?.focus();
          }
        }
        await super.submit();
      }
    }
    customElements.define('k9-fixed-offer', K9FixedOffer);
  }
  if (window.K9NativeOffer) register();
  else document.addEventListener('DOMContentLoaded', register, { once: true });
})();
